import React from 'react';
import {AbsoluteFill, Audio, Sequence, useCurrentFrame, interpolate, useVideoConfig} from 'remotion';
import { Panel, SimpleSlideShowProps, MotionEffect, VideoSyncResult } from '../types';

// Import Img/OffthreadVideo - using require as workaround for TypeScript resolution issue
const { Img } = require('remotion');
const { OffthreadVideo } = require('remotion');

// CSS Properties type alias
type CSSProps = {
  transform?: string;
  transformOrigin?: string;
  opacity?: number;
};

// Transform state accumulated from previous effects
interface TransformState {
  scale?: number;
  translateX?: number;
  translateY?: number;
  rotate?: number;
  transformOrigin?: string;
}

const OVERLAP_SEC = 0.5; // Khoảng thời gian chồng mờ (overlap) giữa 2 panel (tăng lên 0.5s để mượt hơn)
// Bắt đầu decode OffthreadVideo sớm hơn để tránh giật/đen khi chuyển cảnh sang VIDEO panel
const VIDEO_PRELOAD_SEC = 1.5;

const MASTER_FADE_IN_SEC = 0.65;
const MASTER_FADE_OUT_SEC = 1.25;

function masterAudioEnvelope(
  currentTimeSec: number,
  totalDurationSec: number,
  peakVolume: number,
  fadeInSec: number = MASTER_FADE_IN_SEC,
  fadeOutSec: number = MASTER_FADE_OUT_SEC
): number {
  if (totalDurationSec <= 0) return 0;
  if (fadeInSec + fadeOutSec >= totalDurationSec) {
    const mid = totalDurationSec / 2;
    if (currentTimeSec <= mid) {
      return interpolate(currentTimeSec, [0, mid], [0, peakVolume], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      });
    }
    return interpolate(currentTimeSec, [mid, totalDurationSec], [peakVolume, 0], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }
  if (currentTimeSec < fadeInSec) {
    return interpolate(currentTimeSec, [0, fadeInSec], [0, peakVolume], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }
  const outStart = totalDurationSec - fadeOutSec;
  if (currentTimeSec > outStart) {
    return interpolate(currentTimeSec, [outStart, totalDurationSec], [peakVolume, 0], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }
  return peakVolume;
}

type SafeAudioProps = {
  src: string;
  volume: number | ((frame: number) => number);
  loop?: boolean;
  loopVolumeCurveBehavior?: 'repeat' | 'extend';
  playbackRate?: number;
};

// Safe Audio component that won't crash if URL is invalid
const SafeAudio: React.FC<SafeAudioProps> = ({ src, volume, loop, loopVolumeCurveBehavior, playbackRate }) => {
  try {
    if (!src || src.trim() === '') return null;
    return (
      <Audio
        src={src}
        volume={volume}
        playbackRate={playbackRate}
        {...(loop ? { loop: true } : {})}
        {...(loopVolumeCurveBehavior ? { loopVolumeCurveBehavior } : {})}
      />
    );
  } catch (error) {
    console.error('[SafeAudio] Error rendering audio:', error);
    return null;
  }
};

export const SimpleSlideshow: React.FC<SimpleSlideShowProps> = ({
  panels = [],
  panelDurations,
  audioPath,
  backgroundMusicUrl, // Epic 5: BGM
  backgroundVolume = 0.25, // Epic 5: BGM volume
  backgroundColor = 'black',
  textColor = 'white',
  showTitles = false,
  showCaptions = true,
  speedMultiplier = 1.0
}) => {
  const { fps, durationInFrames: totalFrames } = useVideoConfig();
  const defaultDuration = 5000;
  
  // Calculate durations in frames - prioritize startTime/endTime if available
  const durationsInFrames = React.useMemo(() => {
    return panels.map((panel, index) => {
      // Use actual audio timestamps if available
      if (panel.startTime !== undefined && panel.endTime !== undefined) {
        const durationSec = panel.endTime - panel.startTime;
        return Math.ceil((durationSec * fps) / speedMultiplier);
      }
      
      // Fallback to panelDurations array
      const durationMs = panelDurations && panelDurations[index] 
        ? panelDurations[index] 
        : defaultDuration;
      return Math.ceil(((durationMs / 1000) * fps) / speedMultiplier);
    });
  }, [panels, panelDurations, fps, speedMultiplier]);
  
  // Calculate frame offsets - use actual startTime if available, otherwise accumulate
  const frameOffsets = React.useMemo(() => {
    const overlapFrames = Math.ceil((OVERLAP_SEC * fps) / speedMultiplier);
    const offsets: number[] = [];
    
    // Check if all panels have startTime (from audio analysis)
    const hasTimestamps = panels.every(p => p.startTime !== undefined);
    
    if (hasTimestamps) {
      // Use actual audio timestamps - NO overlap subtraction
      offsets.push(...panels.map(p => Math.floor((p.startTime! * fps) / speedMultiplier)));
      console.log('[SimpleSlideshow] Using ACTUAL audio timestamps:', 
        panels.map((p, i) => `Panel ${i}: ${p.startTime}s-${p.endTime}s (frame ${offsets[i]})`));
    } else {
      // Fallback: accumulate with overlap
      offsets.push(0);
      for (let i = 1; i < panels.length; i++) {
        offsets[i] = offsets[i-1] + durationsInFrames[i-1] - overlapFrames;
      }
      console.log('[SimpleSlideshow] Using accumulated offsets (no timestamps):', offsets);
    }
    
    return offsets;
  }, [panels, durationsInFrames, fps, speedMultiplier]);

  return (
    <AbsoluteFill style={{ backgroundColor }}>
      {/* Render all panels with Overlap */}
      {panels.map((panel, index) => {
        const isFirst = index === 0;
        const isLast = index === panels.length - 1;
        const overlapFrames = Math.ceil((OVERLAP_SEC * fps) / speedMultiplier);
        
        // Tăng duration của sequence để panel hiện tại kéo dài sang cả panel sau
        // Panel cuối cùng extend đến hết video để không bị màn hình đen
        let extendedDuration = durationsInFrames[index] + (isLast ? 0 : overlapFrames);
        
        if (isLast) {
          // Panel cuối: extend đến hết totalFrames
          const remainingFrames = totalFrames - frameOffsets[index];
          extendedDuration = Math.max(durationsInFrames[index], remainingFrames);
        }

        return (
          <Sequence
            key={`panel-${panel.id || index}`}
            from={frameOffsets[index]}
            durationInFrames={extendedDuration}
            layout="none"
          >
            <PanelWithEffects
              panel={panel}
              panelDurationSec={extendedDuration / (fps / speedMultiplier)}
              fps={fps}
              speedMultiplier={speedMultiplier}
              backgroundColor={backgroundColor}
              textColor={textColor}
              showTitles={showTitles}
              showCaptions={showCaptions}
              isFirst={isFirst}
              isLast={isLast}
              overlapSec={OVERLAP_SEC}
            />
          </Sequence>
        );
      })}

      {/* VIDEO panel preload: render OffthreadVideo ẩn sớm hơn để tránh giật khi chuyển cảnh
          - Preview: tránh Remotion Player vào buffering state giữa chừng
          - Render: warm up OffthreadVideo worker/cache trước khi overlap frames cần dùng */}
      {panels.map((panel, index) => {
        if (panel.mediaType !== 'VIDEO' || !panel.videoUrl) return null;
        const naturalStartFrame = frameOffsets[index];
        const preloadCount = Math.ceil((VIDEO_PRELOAD_SEC * fps) / speedMultiplier);
        const preloadStart = Math.max(0, naturalStartFrame - preloadCount);
        const preloadDuration = naturalStartFrame - preloadStart;
        if (preloadDuration <= 0) return null;
        return (
          <Sequence
            key={`preload-${panel.id || index}`}
            from={preloadStart}
            durationInFrames={preloadDuration}
            layout="none"
          >
            <AbsoluteFill style={{ opacity: 0, pointerEvents: 'none' }}>
              <OffthreadVideo
                src={panel.videoUrl}
                style={{ position: 'absolute', width: 1, height: 1, top: -9999 }}
                muted
              />
            </AbsoluteFill>
          </Sequence>
        );
      })}

      {/* Voice — fade in/out theo full composition (khớp bản render video-service) */}
      {audioPath && (
        <SafeAudio
          src={audioPath}
          playbackRate={speedMultiplier}
          volume={(frame) => masterAudioEnvelope(frame / fps, totalFrames / fps, 1)}
        />
      )}

      {/* BGM — full length composition + loop; tránh cắt sớm do Sequence sum(panel durations) */}
      {backgroundMusicUrl && (
        <SafeAudio
          src={backgroundMusicUrl}
          loop
          loopVolumeCurveBehavior="extend"
          playbackRate={speedMultiplier}
          volume={(frame) => masterAudioEnvelope(frame / fps, totalFrames / fps, backgroundVolume)}
        />
      )}

      {/* Epic 5: Sound Effects (per panel) */}
      {panels.flatMap((panel, panelIndex) => {
        if (!panel.soundEffects || panel.soundEffects.length === 0) return [];
        
        const panelStartFrame = frameOffsets[panelIndex];
        if (panelStartFrame === undefined || isNaN(panelStartFrame)) return [];
        
        return panel.soundEffects
          .filter((sfx: any) => sfx.audioUrl && sfx.audioUrl.trim() !== '')
          .map((sfx: any) => {
            // triggerTime is in seconds (relative to panel start)
            const sfxStartFrame = panelStartFrame + Math.floor((sfx.triggerTime * fps) / speedMultiplier);
            if (isNaN(sfxStartFrame) || sfxStartFrame < 0) return null;
            
            // Duration in frames (default 1.5s if not specified)
            const sfxDuration = sfx.duration || 1.5;
            const sfxDurationFrames = Math.floor((sfxDuration * fps) / speedMultiplier);
            
            return (
              <Sequence 
                key={`${panel.id}-${sfx.id}`} 
                from={sfxStartFrame}
                durationInFrames={sfxDurationFrames}
              >
                <SafeAudio 
                  src={sfx.audioUrl} 
                  volume={sfx.volume || 1.0} 
                  playbackRate={speedMultiplier} 
                />
              </Sequence>
            );
          })
          .filter(Boolean);
      })}
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────────
// Epic 7: Video sync logic
// ─────────────────────────────────────────────────────────────

const SYNC_THRESHOLD = 0.10;

export function calculateVideoSync(audioDuration: number, videoDuration: number): VideoSyncResult {
  if (!videoDuration || !audioDuration) return { strategy: 'sync', playbackRate: 1.0 };

  if (videoDuration > audioDuration * (1 + SYNC_THRESHOLD)) {
    return { strategy: 'trim', playbackRate: 1.0, endAt: audioDuration };
  }

  const diff = Math.abs(audioDuration - videoDuration) / audioDuration;
  if (diff <= SYNC_THRESHOLD) {
    return { strategy: 'sync', playbackRate: videoDuration / audioDuration };
  }

  const pad = (audioDuration - videoDuration) / 2;
  return { strategy: 'centered', playbackRate: 1.0, padBeforeSec: pad, padAfterSec: pad };
}

// ─────────────────────────────────────────────────────────────
// Epic 7: VideoPanelRenderer
// ─────────────────────────────────────────────────────────────

interface VideoPanelRendererProps {
  panel: Panel;
  panelDurationSec: number;
  fps: number;
  speedMultiplier: number;
  transitionOpacity: number;
  backgroundColor: string;
  textColor: string;
  showCaptions: boolean;
}

const VideoPanelRenderer: React.FC<VideoPanelRendererProps> = ({
  panel,
  panelDurationSec,
  fps,
  speedMultiplier,
  transitionOpacity,
  backgroundColor,
  textColor,
  showCaptions,
}) => {
  const frame = useCurrentFrame();
  const currentTimeSec = (frame / fps) * speedMultiplier;

  const syncResult = calculateVideoSync(panelDurationSec, panel.videoDuration || 0);

  const safePlaybackRate = (rate: number) => Math.min(2.0, Math.max(0.5, rate * speedMultiplier));

  if (syncResult.strategy === 'centered') {
    const videoStartSec = syncResult.padBeforeSec!;
    const videoEndSec = videoStartSec + (panel.videoDuration ?? 0);

    const inBefore = currentTimeSec < videoStartSec;
    const inVideo  = currentTimeSec >= videoStartSec && currentTimeSec < videoEndSec;
    const inAfter  = currentTimeSec >= videoEndSec;

    const scaleBefore = interpolate(currentTimeSec, [0, videoStartSec], [1.15, 1.0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const scaleAfter  = interpolate(currentTimeSec, [videoEndSec, panelDurationSec], [1.0, 1.15], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

    const fallbackImageSrc = panel.firstFrameUrl || panel.imagePath;

    return (
      <AbsoluteFill style={{ backgroundColor: panel.backgroundColor || backgroundColor, overflow: 'hidden', opacity: transitionOpacity }}>
        {inBefore && panel.firstFrameUrl && (
          <Img 
            src={panel.firstFrameUrl} 
            style={{ 
              position: 'absolute', width: '100%', height: '100%', objectFit: 'cover',
              transform: `scale(${scaleBefore})` 
            }} 
          />
        )}
        {inVideo && panel.videoUrl && (
          <OffthreadVideo 
            src={panel.videoUrl} 
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
            playbackRate={safePlaybackRate(1.0)} 
            muted 
          />
        )}
        {inAfter && fallbackImageSrc && (
          <Img 
            src={panel.imagePath || panel.firstFrameUrl} 
            style={{ 
              position: 'absolute', width: '100%', height: '100%', objectFit: 'cover',
              transform: `scale(${scaleAfter})` 
            }} 
          />
        )}
        <AbsoluteFill style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          padding: '2rem',
          background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}>
          {showCaptions && panel.caption && (
            <p style={{ color: textColor, fontSize: '1.5rem', margin: 0, textShadow: '0 1px 2px rgba(0,0,0,0.5)', maxWidth: '80%' }}>
              {panel.caption}
            </p>
          )}
        </AbsoluteFill>
      </AbsoluteFill>
    );
  }

  // trim or sync
  return (
    <AbsoluteFill style={{ backgroundColor: panel.backgroundColor || backgroundColor, overflow: 'hidden', opacity: transitionOpacity }}>
      {panel.videoUrl && (
        <OffthreadVideo
          src={panel.videoUrl}
          style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
          playbackRate={safePlaybackRate(syncResult.playbackRate)}
          endAt={(typeof syncResult.endAt === 'number' && syncResult.endAt > 0) ? Math.floor(syncResult.endAt * fps) : undefined}
          muted
        />
      )}
      <AbsoluteFill style={{
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: '2rem',
        background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.7) 100%)',
        pointerEvents: 'none',
      }}>
        {showCaptions && panel.caption && (
          <p style={{ color: textColor, fontSize: '1.5rem', margin: 0, textShadow: '0 1px 2px rgba(0,0,0,0.5)', maxWidth: '80%' }}>
            {panel.caption}
          </p>
        )}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ─────────────────────────────────────────────────────────────

interface PanelWithEffectsProps {
  panel: Panel;
  panelDurationSec: number;
  fps: number;
  speedMultiplier: number;
  backgroundColor: string;
  textColor: string;
  showTitles: boolean;
  showCaptions: boolean;
  isFirst: boolean;
  isLast: boolean;
  overlapSec: number;
}

const PanelWithEffects: React.FC<PanelWithEffectsProps> = ({
  panel,
  panelDurationSec,
  fps,
  speedMultiplier,
  backgroundColor,
  textColor,
  showTitles,
  showCaptions,
  isFirst,
  isLast,
  overlapSec,
}) => {
  const frame = useCurrentFrame();
  const currentTimeSec = (frame / fps) * speedMultiplier;
  
  // LOGIC CHUYỂN CẢNH (CROSS-FADE):
  // 1. Panel đầu tiên: Hiện ngay lập tức (opacity 1)
  // 2. Các panel sau: Mờ dần lên (fade-in) trong khoảng overlapSec để đè lên panel trước
  let transitionOpacity = 1;
  if (!isFirst && currentTimeSec < overlapSec) {
    transitionOpacity = interpolate(currentTimeSec, [0, overlapSec], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    });
  }

  // Epic 7: Video panel có videoUrl → dùng VideoPanelRenderer, bỏ qua motion effects
  if (panel.mediaType === 'VIDEO' && !!panel.videoUrl) {
    return (
      <VideoPanelRenderer
        panel={panel}
        panelDurationSec={panelDurationSec}
        fps={fps}
        speedMultiplier={speedMultiplier}
        transitionOpacity={transitionOpacity}
        backgroundColor={backgroundColor}
        textColor={textColor}
        showCaptions={showCaptions}
      />
    );
  }

  // Phase 5: VIDEO panel nhưng videoUrl = null → hiển thị static image (firstFrameUrl hoặc imagePath)
  if (panel.mediaType === 'VIDEO') {
    const fallbackSrc = panel.firstFrameUrl || panel.imagePath;
    return (
      <AbsoluteFill style={{ backgroundColor: panel.backgroundColor || backgroundColor, overflow: 'hidden', opacity: transitionOpacity }}>
        {fallbackSrc && (
          <Img
            src={fallbackSrc}
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <AbsoluteFill style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          padding: '2rem',
          background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}>
          {showCaptions && panel.caption && (
            <p style={{ color: textColor, fontSize: '1.5rem', margin: 0, textShadow: '0 1px 2px rgba(0,0,0,0.5)', maxWidth: '80%' }}>
              {panel.caption}
            </p>
          )}
        </AbsoluteFill>
      </AbsoluteFill>
    );
  }

  // Removed fade-out for last panel to show full duration
  // if (isLast) {
  //   const fadeOutStart = panelDurationSec - overlapSec;
  //   if (currentTimeSec > fadeOutStart) {
  //     const fadeOutOpacity = interpolate(currentTimeSec, [fadeOutStart, panelDurationSec], [1, 0], {
  //       extrapolateLeft: 'clamp',
  //       extrapolateRight: 'clamp',
  //     });
  //     transitionOpacity *= fadeOutOpacity;
  //   }
  // }
  
  const primaryEffects = (panel.motionEffects || []).filter(e => !e.overlay);
  const overlayEffects = (panel.motionEffects || []).filter(e => e.overlay);
  
  // Progress để tính toán hiệu ứng (giới hạn trong panelDuration gốc)
  const effectTimeSec = Math.min(currentTimeSec, panelDurationSec);

  let activePrimaryEffect: MotionEffect | undefined;
  let activePrimaryIndex = -1;
  for (let i = 0; i < primaryEffects.length; i++) {
    const effect = primaryEffects[i];
    if (effectTimeSec >= effect.startOffset && 
        effectTimeSec < effect.startOffset + effect.duration) {
      activePrimaryEffect = effect;
      activePrimaryIndex = i;
      break;
    }
  }
  
  // Nếu không có active effect, tìm effect gần nhất đã qua
  if (!activePrimaryEffect && primaryEffects.length > 0) {
    for (let i = primaryEffects.length - 1; i >= 0; i--) {
      if (effectTimeSec >= primaryEffects[i].startOffset + primaryEffects[i].duration) {
        activePrimaryIndex = i + 1; // Đặt index sau effect vừa kết thúc
        break;
      }
    }
  }
  
  const activeOverlayEffects = overlayEffects.filter(e =>
    effectTimeSec >= e.startOffset && effectTimeSec < e.startOffset + e.duration
  );
  
  const baseTransformState = calculateBaseTransformState(primaryEffects, activePrimaryIndex);
  
  // Cố định transformOrigin cho toàn bộ panel
  const firstPrimaryEffect = primaryEffects[0];
  const firstFocusPoint = firstPrimaryEffect?.focusPoint || { x: 0.5, y: 0.5 };
  const panelTransformOrigin = `${firstFocusPoint.x * 100}% ${firstFocusPoint.y * 100}%`;
  
  const BASE_SCALE = 1;
  
  let primaryStyle: CSSProps;
  if (activePrimaryEffect) {
    primaryStyle = getEffectStyle(activePrimaryEffect, effectTimeSec, baseTransformState, BASE_SCALE, panelTransformOrigin);
  } else {
    const allPreviousState = calculateBaseTransformState(primaryEffects, primaryEffects.length);
    const transformParts: string[] = [];
    if (allPreviousState.translateX !== 0 || allPreviousState.translateY !== 0) {
      transformParts.push(`translate(${allPreviousState.translateX}%, ${allPreviousState.translateY}%)`);
    }
    // Apply BASE_SCALE consistently (multiply, not max)
    const finalScale = (allPreviousState.scale || 1) * BASE_SCALE;
    if (finalScale !== 1) transformParts.push(`scale(${finalScale})`);
    if (allPreviousState.rotate !== 0) transformParts.push(`rotate(${allPreviousState.rotate}deg)`);
    
    primaryStyle = {
      transform: transformParts.length > 0 ? transformParts.join(' ') : `scale(${BASE_SCALE})`,
      transformOrigin: panelTransformOrigin || allPreviousState.transformOrigin,
      opacity: 1
    };
  }
  
  const overlayStyles = activeOverlayEffects.map(e => 
    getEffectStyle(e, effectTimeSec, {}, BASE_SCALE, panelTransformOrigin)
  );
  
  const finalStyle = combineStyles(primaryStyle, overlayStyles);
  
  // Ease back to neutral before panel ends (smooth transition to next panel)
  if (!isLast && currentTimeSec > panelDurationSec - overlapSec) {
    // In the last overlap period, gradually reset translate to 0 for smoother transition
    const easeProgress = (currentTimeSec - (panelDurationSec - overlapSec)) / overlapSec;
    const easeT = interpolate(easeProgress, [0, 1], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: (t) => t * t * (3 - 2 * t) // smooth ease
    });
    
    // Parse current transform
    const currentTransform = finalStyle.transform || '';
    const translateMatch = currentTransform.match(/translate\(([^,]+)%?,\s*([^)]+)%?\)/);
    
    if (translateMatch) {
      const currentX = parseFloat(translateMatch[1]);
      const currentY = parseFloat(translateMatch[2]);
      const easedX = interpolate(easeT, [0, 1], [currentX, 0]);
      const easedY = interpolate(easeT, [0, 1], [currentY, 0]);
      
      // Replace translate with eased values
      finalStyle.transform = currentTransform.replace(
        /translate\([^)]+\)/,
        `translate(${easedX}%, ${easedY}%)`
      );
    }
  }
  
  finalStyle.opacity = (finalStyle.opacity !== undefined ? finalStyle.opacity : 1) * transitionOpacity;
  
  // Validate imagePath
  if (!panel.imagePath || panel.imagePath.trim() === '') {
    console.error('[SimpleSlideshow] Panel missing imagePath:', panel.id);
    return (
      <AbsoluteFill style={{ backgroundColor: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#fff', fontSize: '1.5rem', textAlign: 'center', padding: '2rem' }}>
          <p>⚠️ Panel {panel.id} missing image</p>
          {panel.caption && <p style={{ fontSize: '1rem', marginTop: '1rem', opacity: 0.7 }}>{panel.caption}</p>}
        </div>
      </AbsoluteFill>
    );
  }

  return (
    <AbsoluteFill style={{ backgroundColor: panel.backgroundColor || backgroundColor, overflow: 'hidden' }}>
      <Img
        src={panel.imagePath}
        style={{
          ...finalStyle,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          willChange: 'transform, opacity',
        }}
      />
      <AbsoluteFill style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: '2rem', background: 'linear-gradient(transparent 60%, rgba(0,0,0,0.7) 100%)', pointerEvents: 'none' }}>
        {showTitles && panel.title && <h1 style={{ color: textColor, fontSize: '3rem', margin: '0 0 1rem 0', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{panel.title}</h1>}
        {showCaptions && panel.caption && <p style={{ color: textColor, fontSize: '1.5rem', margin: 0, textShadow: '0 1px 2px rgba(0,0,0,0.5)', maxWidth: '80%' }}>{panel.caption}</p>}
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

// ... (Các hàm helper calculateBaseTransformState, getEffectEndState, getEffectStyle, combineStyles giữ nguyên như cũ)
function calculateBaseTransformState(primaryEffects: MotionEffect[], currentEffectIndex: number): TransformState {
  const state: TransformState = { scale: 1, translateX: 0, translateY: 0, rotate: 0 };
  if (currentEffectIndex <= 0) return state;
  for (let i = 0; i < currentEffectIndex; i++) {
    const effect = primaryEffects[i];
    const isFirstEffect = (i === 0);
    const endState = getEffectEndState(effect, isFirstEffect);
    if (endState.scale !== undefined) state.scale = state.scale! * endState.scale;
    if (endState.translateX !== undefined) state.translateX = state.translateX! + endState.translateX;
    if (endState.translateY !== undefined) state.translateY = state.translateY! + endState.translateY;
    if (endState.rotate !== undefined) state.rotate = state.rotate! + endState.rotate;
    if (endState.transformOrigin) state.transformOrigin = endState.transformOrigin;
  }
  return state;
}

function getEffectEndState(effect: MotionEffect, isFirstEffect: boolean = false): TransformState {
  const { focusPoint = { x: 0.5, y: 0.5 }, intensity } = effect;
  const state: TransformState = {};
  switch (effect.type) {
    case 'zoom-in': 
      state.scale = 1 + intensity * 0.3; 
      state.transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`; 
      break;
    case 'zoom-out':
      // V3.1: Zoom-out có thể ở đầu panel NHƯNG phải có startZoom
      const metadata = (effect as any).metadata || {};
      const startZoomScale = metadata.startZoom || 1.3; // Default bắt đầu đã zoom in
      
      if (isFirstEffect && effect.startOffset === 0) {
        // Zoom-out ở đầu: Kết thúc ở base scale (1.0)
        state.scale = 1.0;
      } else {
        // Zoom-out sau effect khác: Kết thúc ở scale nhỏ hơn
        state.scale = 1 / (1 + intensity * 0.3);
      }
      state.transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`; 
      break;
    case 'pan-left': state.translateX = -intensity * 20; break;
    case 'pan-right': state.translateX = intensity * 20; break;
    case 'pan-up': state.translateY = -intensity * 20; break;
    case 'pan-down': state.translateY = intensity * 20; break;
    
    // V3: New effects
    case 'ken-burns': {
      // Ken-burns is composite: zoom-in + pan
      const metadata = (effect as any).metadata || {};
      const zoomIntensity = metadata.zoomIntensity || intensity * 0.6;
      const panIntensity = metadata.panIntensity || intensity * 0.4;
      const panDirection = metadata.panDirection || 'right';
      
      state.scale = 1 + zoomIntensity * 0.3;
      state.transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      
      // Add pan translation based on direction
      if (panDirection === 'left') state.translateX = -panIntensity * 20;
      else if (panDirection === 'right') state.translateX = panIntensity * 20;
      else if (panDirection === 'up') state.translateY = -panIntensity * 20;
      else if (panDirection === 'down') state.translateY = panIntensity * 20;
      break;
    }
    
    case 'static':
      // No transformation - stays at current state
      break;
    
    case 'slow-pan': {
      // Slow pan with optional slight zoom
      const metadata = (effect as any).metadata || {};
      const direction = metadata.direction || 'horizontal';
      const startZoom = metadata.startZoom || 1.0;
      
      if (startZoom > 1) state.scale = startZoom;
      
      if (direction === 'horizontal') {
        state.translateX = intensity * 20; // Default right
      } else {
        state.translateY = intensity * 20; // Default down
      }
      break;
    }
    
    case 'zoom-in-out':
      // Ends at base scale (zoomed back out)
      state.scale = 1.0;
      state.transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      break;
    
    case 'double-zoom': {
      // Ends zoomed with pan to second focus
      const metadata = (effect as any).metadata || {};
      const firstFocus = metadata.firstFocus || focusPoint;
      const secondFocus = metadata.secondFocus || focusPoint;
      
      state.scale = 1 + intensity * 0.3;
      state.transformOrigin = `${firstFocus.x * 100}% ${firstFocus.y * 100}%`;
      
      // Calculate final translate to show second focus
      const deltaX = (secondFocus.x - firstFocus.x) * 100;
      const deltaY = (secondFocus.y - firstFocus.y) * 100;
      state.translateX = (state.translateX || 0) - deltaX * 0.8;
      state.translateY = (state.translateY || 0) - deltaY * 0.8;
      break;
    }
    
    case 'slide': state.translateX = -100; break;
    case 'fade': break;
    case 'shake': case 'rotate': break;
  }
  return state;
}

function getEffectStyle(effect: MotionEffect, currentTimeSec: number, baseState: TransformState, baseScale: number = 1, panelTransformOrigin?: string): CSSProps {
  const t = Math.max(0, Math.min(1, (currentTimeSec - effect.startOffset) / effect.duration));
  const { focusPoint = { x: 0.5, y: 0.5 }, intensity } = effect;
  
  // Cinematic easing functions
  const cinematicEaseOut = (t: number) => 1 - Math.pow(1 - t, 3); // Cubic ease-out
  const cinematicEaseInOut = (t: number) => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; // Cubic ease-in-out
  const smoothEase = (t: number) => t * t * (3 - 2 * t); // Smoothstep (original)
  
  // Apply BASE_SCALE to baseState.scale (accumulated multiplier from previous effects)
  const currentScale = (baseState.scale || 1) * baseScale;
  const currentTranslateX = baseState.translateX || 0;
  const currentTranslateY = baseState.translateY || 0;
  const currentRotate = baseState.rotate || 0;
  const transformParts: string[] = [];
  let transformOrigin = panelTransformOrigin || baseState.transformOrigin || `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
  let opacity = 1;
  
  switch (effect.type) {
    case 'zoom-in': {
      // Cinematic zoom with ease-out (starts fast, ends slow)
      const scale = interpolate(t, [0, 1], [currentScale, currentScale * (1 + intensity * 0.3)], { easing: cinematicEaseOut });
      // Push translate first to preserve position
      if (currentTranslateX !== 0 || currentTranslateY !== 0) {
        transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      }
      transformParts.push(`scale(${scale})`);
      if (!panelTransformOrigin) transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      break;
    }
    case 'zoom-out': {
      // V3.1: Zoom-out LUÔN bắt đầu từ trạng thái đã zoom-in
      const metadata = (effect as any).metadata || {};
      const startZoomScale = metadata.startZoom || 1.3; // Default: start zoomed in at 1.3x
      
      let startScale = currentScale;
      let endScale = currentScale / (1 + intensity * 0.3);
      
      if (effect.startOffset === 0 && baseState.scale === 1) {
        // Zoom-out ở đầu panel: BẮT ĐẦU từ đã zoom in, zoom RA về base scale
        startScale = baseScale * startZoomScale; // Start zoomed in (e.g., 1.3x)
        endScale = baseScale; // End at base scale (1.0x)
      }
      
      // Cinematic easing: ease-in-out cubic (smoother than smoothstep)
      const cinematicEase = (t: number) => {
        return t < 0.5 
          ? 4 * t * t * t 
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      };
      
      const scale = interpolate(t, [0, 1], [startScale, endScale], { easing: cinematicEase });
      
      // Push translate first to preserve position
      if (currentTranslateX !== 0 || currentTranslateY !== 0) {
        transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      }
      transformParts.push(`scale(${scale})`);
      if (!panelTransformOrigin) transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      break;
    }
    
    // V3: Ken-Burns Effect (Zoom + Pan composite)
    case 'ken-burns': {
      const metadata = (effect as any).metadata || {};
      const zoomDirection = metadata.zoomDirection || 'in';
      const panDirection = metadata.panDirection || 'right';
      const zoomIntensity = metadata.zoomIntensity || intensity * 0.6;
      const panIntensity = metadata.panIntensity || intensity * 0.4;
      
      // Split duration: 50% zoom, 50% pan (can overlap)
      const zoomPhase = Math.min(1, t * 1.5); // Zoom completes at 67%
      const panPhase = Math.max(0, (t - 0.33) * 1.5); // Pan starts at 33%
      
      // Calculate zoom
      let scale;
      if (zoomDirection === 'in') {
        scale = interpolate(zoomPhase, [0, 1], [currentScale, currentScale * (1 + zoomIntensity * 0.3)], { easing: (t) => t * t * (3 - 2 * t) });
      } else {
        scale = interpolate(zoomPhase, [0, 1], [currentScale * (1 + zoomIntensity * 0.3), currentScale], { easing: (t) => t * t * (3 - 2 * t) });
      }
      
      // Calculate pan
      let translateX = currentTranslateX;
      let translateY = currentTranslateY;
      
      if (panDirection === 'left') {
        translateX = interpolate(panPhase, [0, 1], [currentTranslateX, currentTranslateX - panIntensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      } else if (panDirection === 'right') {
        translateX = interpolate(panPhase, [0, 1], [currentTranslateX, currentTranslateX + panIntensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      } else if (panDirection === 'up') {
        translateY = interpolate(panPhase, [0, 1], [currentTranslateY, currentTranslateY - panIntensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      } else if (panDirection === 'down') {
        translateY = interpolate(panPhase, [0, 1], [currentTranslateY, currentTranslateY + panIntensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      }
      
      transformParts.push(`translate(${translateX}%, ${translateY}%)`);
      transformParts.push(`scale(${scale})`);
      if (!panelTransformOrigin) transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      break;
    }
    
    // V3: Static (No motion)
    case 'static': {
      // No animation - just maintain current state
      if (currentTranslateX !== 0 || currentTranslateY !== 0) {
        transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      }
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      if (currentRotate !== 0) transformParts.push(`rotate(${currentRotate}deg)`);
      break;
    }
    
    // V3: Slow Pan (for panoramas)
    case 'slow-pan': {
      const metadata = (effect as any).metadata || {};
      const direction = metadata.direction || 'horizontal';
      const startZoom = metadata.startZoom || 1.0;
      
      // Optional slight zoom for safety
      const scale = startZoom > 1 ? currentScale * startZoom : currentScale;
      
      let translateX = currentTranslateX;
      let translateY = currentTranslateY;
      
      if (direction === 'horizontal') {
        translateX = interpolate(t, [0, 1], [currentTranslateX, currentTranslateX + intensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      } else {
        translateY = interpolate(t, [0, 1], [currentTranslateY, currentTranslateY + intensity * 20], { easing: (t) => t * t * (3 - 2 * t) });
      }
      
      transformParts.push(`translate(${translateX}%, ${translateY}%)`);
      if (scale !== 1) transformParts.push(`scale(${scale})`);
      break;
    }
    
    // V3: Zoom-In-Out (Breathing effect)
    case 'zoom-in-out': {
      const metadata = (effect as any).metadata || {};
      const peakTime = metadata.peakTime || 0.5; // When to reach peak zoom
      
      let scale;
      if (t < peakTime) {
        // Zoom in phase
        const phase = t / peakTime;
        scale = interpolate(phase, [0, 1], [currentScale, currentScale * (1 + intensity * 0.3)], { easing: (t) => t * t * (3 - 2 * t) });
      } else {
        // Zoom out phase
        const phase = (t - peakTime) / (1 - peakTime);
        scale = interpolate(phase, [0, 1], [currentScale * (1 + intensity * 0.3), currentScale], { easing: (t) => t * t * (3 - 2 * t) });
      }
      
      if (currentTranslateX !== 0 || currentTranslateY !== 0) {
        transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      }
      transformParts.push(`scale(${scale})`);
      if (!panelTransformOrigin) transformOrigin = `${focusPoint.x * 100}% ${focusPoint.y * 100}%`;
      break;
    }
    
    // V3: Double Zoom (Two focus points)
    case 'double-zoom': {
      const metadata = (effect as any).metadata || {};
      const firstFocus = metadata.firstFocus || focusPoint;
      const secondFocus = metadata.secondFocus || focusPoint;
      const splitTime = metadata.splitTime || 0.5;
      
      let scale = currentScale * (1 + intensity * 0.3);
      let currentFocus;
      
      if (t < splitTime) {
        // Phase 1: Zoom to first focus
        const phase = t / splitTime;
        scale = interpolate(phase, [0, 1], [currentScale, currentScale * (1 + intensity * 0.3)], { easing: (t) => t * t * (3 - 2 * t) });
        currentFocus = firstFocus;
        transformOrigin = `${firstFocus.x * 100}% ${firstFocus.y * 100}%`;
      } else {
        // Phase 2: Pan to second focus while maintaining zoom
        const phase = (t - splitTime) / (1 - splitTime);
        
        // Keep transform origin at first focus, but use translate to move view to second focus
        transformOrigin = `${firstFocus.x * 100}% ${firstFocus.y * 100}%`;
        currentFocus = firstFocus;
        
        // Calculate how much to pan to center the second focus
        const deltaX = (secondFocus.x - firstFocus.x) * 100;
        const deltaY = (secondFocus.y - firstFocus.y) * 100;
        
        // Pan smoothly to second focus
        const panX = interpolate(phase, [0, 1], [0, -deltaX * 0.8], { easing: (t) => t * t * (3 - 2 * t) });
        const panY = interpolate(phase, [0, 1], [0, -deltaY * 0.8], { easing: (t) => t * t * (3 - 2 * t) });
        
        transformParts.push(`translate(${currentTranslateX + panX}%, ${currentTranslateY + panY}%)`);
        transformParts.push(`scale(${scale})`);
        break;
      }
      
      if (currentTranslateX !== 0 || currentTranslateY !== 0) {
        transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      }
      transformParts.push(`scale(${scale})`);
      break;
    }
    
    case 'pan-left': {
      // Cinematic pan with ease-in-out (smooth acceleration and deceleration)
      const translateX = interpolate(t, [0, 1], [currentTranslateX, currentTranslateX - intensity * 20], { easing: cinematicEaseInOut });
      transformParts.push(`translate(${translateX}%, ${currentTranslateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'pan-right': {
      const translateX = interpolate(t, [0, 1], [currentTranslateX, currentTranslateX + intensity * 20], { easing: cinematicEaseInOut });
      transformParts.push(`translate(${translateX}%, ${currentTranslateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'pan-up': {
      const translateY = interpolate(t, [0, 1], [currentTranslateY, currentTranslateY - intensity * 20], { easing: cinematicEaseInOut });
      transformParts.push(`translate(${currentTranslateX}%, ${translateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'pan-down': {
      const translateY = interpolate(t, [0, 1], [currentTranslateY, currentTranslateY + intensity * 20], { easing: cinematicEaseInOut });
      transformParts.push(`translate(${currentTranslateX}%, ${translateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'slide': {
      const translateX = interpolate(t, [0, 0.8, 1], [currentTranslateX, currentTranslateX, -100]);
      opacity = interpolate(t, [0, 0.8, 1], [1, 1, 0]);
      transformParts.push(`translateX(${translateX}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'fade':
      opacity = interpolate(t, [0, 0.8, 1], [1, 1, 0.1]);
      if (currentTranslateX !== 0 || currentTranslateY !== 0) transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      if (currentRotate !== 0) transformParts.push(`rotate(${currentRotate}deg)`);
      break;
    case 'shake': {
      const shakePhase = t * 10 * Math.PI * 2;
      const shakeX = Math.sin(shakePhase) * (intensity * 5);
      const shakeY = Math.cos(shakePhase) * (intensity * 5);
      transformParts.push(`translate(calc(${currentTranslateX}% + ${shakeX}px), calc(${currentTranslateY}% + ${shakeY}px))`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    case 'rotate': {
      const additionalRotation = Math.sin(t * Math.PI * 2) * (intensity * 5);
      transformParts.push(`rotate(${currentRotate + additionalRotation}deg)`);
      if (currentTranslateX !== 0 || currentTranslateY !== 0) transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      break;
    }
    default:
      if (currentTranslateX !== 0 || currentTranslateY !== 0) transformParts.push(`translate(${currentTranslateX}%, ${currentTranslateY}%)`);
      if (currentScale !== 1) transformParts.push(`scale(${currentScale})`);
      if (currentRotate !== 0) transformParts.push(`rotate(${currentRotate}deg)`);
      break;
  }
  
  return { transform: transformParts.length > 0 ? transformParts.join(' ') : undefined, transformOrigin, opacity };
}

function combineStyles(primaryStyle: CSSProps, overlayStyles: CSSProps[]): CSSProps {
  if (overlayStyles.length === 0) return primaryStyle;
  const transforms: string[] = [];
  let opacity = primaryStyle.opacity !== undefined ? primaryStyle.opacity : 1;
  if (primaryStyle.transform) transforms.push(primaryStyle.transform);
  overlayStyles.forEach(style => {
    if (style.transform) transforms.push(style.transform);
    if (style.opacity !== undefined) opacity *= style.opacity;
  });
  return { transform: transforms.length > 0 ? transforms.join(' ') : undefined, transformOrigin: primaryStyle.transformOrigin, opacity };
}
